import { tenant } from '@tenant';
import { default as Modal } from 'antd/es/modal';

import { formatDecimalAmount } from 'utils/formatters/formatCurrency';

import { useGetDisbursementPayoutRequestsUniqueReference } from 'api';

import { useOrganisationId } from 'hooks/context';

import { BlindingDots } from '@atoms/BlindingDots/BlindingDots';
import { PrimaryButton } from '@atoms/buttons/PrimaryButton';
import { SecondaryButton } from '@atoms/buttons/SecondaryButton';
import { DetailsPayoutAttachmentSectionContainer } from '@atoms/DetailsAttachmentSection/DetailsPayoutAttachmentSectionContainer';
import { RollingLoader } from '@atoms/RollingLoader/RollingLoader';

import { CheckCircleIcon } from 'components/ui/atoms/Icon';

import { SUCCESS_TEST_ID, VIEW_PAYOUT_REQUESTS_TEST_ID } from '../constants';

type Props = {
  onClose: () => void;
  reference: string;
};

export const AcceptanceDialog = ({ onClose, reference }: Props) => {
  const organisationId = useOrganisationId<number>();
  const { data, isLoading, isSuccess } =
    useGetDisbursementPayoutRequestsUniqueReference(organisationId, reference, {
      query: { select: (resp) => resp.data }
    });

  return (
    <Modal
      open={true}
      onCancel={onClose}
      footer={null}
      width={600}
      closable={false}
      maskClosable={false}
    >
      {data && (
        <div className="p-6">
          {isLoading && (
            <div className="flex flex-col items-center gap-y-4 rounded-b-lg bg-surface-muted p-4">
              <RollingLoader ariaLabel="Processing" />
              <div className="text-xs">
                <span>Processing</span>
                <BlindingDots />
              </div>
            </div>
          )}
          {isSuccess && (
            <div className="flex flex-col gap-4">
              <div className="flex flex-col items-center gap-4">
                <CheckCircleIcon className="mr-1 w-12 stroke-content-subtle" />
                <div
                  data-testid={SUCCESS_TEST_ID}
                  className="text-lg font-bold"
                >
                  Payout request successfully created
                </div>
                <div className="text-3xl font-bold">
                  {data.currency} {formatDecimalAmount(Number(data.amount))}
                </div>
              </div>
              <div className="bg flex flex-col rounded-2xl border bg-surface-muted p-4">
                <div>
                  End-to-end ID:
                  <span className="ml-1 font-semibold">
                    {data.end_to_end_id}
                  </span>
                </div>
                <div>
                  {tenant.name.company} reference number:
                  <span className="ml-1 font-semibold">{reference} </span>
                </div>
              </div>
              <div className="pt-4">
                For more information and tracking of this request please check
                payout requests with the provided End-to-end ID.
              </div>
              <div className="mt-4">
                <div className="text-sm font-medium">Attach files</div>
                <div className="pb-2 text-xs text-content-strong">
                  Add supporting documents (optional).
                </div>
                <DetailsPayoutAttachmentSectionContainer
                  resourceId={reference}
                />
              </div>
            </div>
          )}
          <div className="flex w-full flex-col gap-4 pt-6">
            <SecondaryButton
              className="w-full"
              size="large"
              disabled={isLoading}
              onClick={onClose}
              data-testid={VIEW_PAYOUT_REQUESTS_TEST_ID}
            >
              View payout requests
            </SecondaryButton>
            <PrimaryButton
              className="w-full"
              size="large"
              disabled={isLoading}
              onClick={onClose}
            >
              Finish and close
            </PrimaryButton>
          </div>
        </div>
      )}
    </Modal>
  );
};
